import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FormGroup, Select, Input, Button, Textarea } from '../components/Form';
import {
  getAgendadores,
  getTodasPessoas,
  getHistoricoAgendamentos,
  getHistoricoVendas,
  getRegistroEnviosDiarios,
  updateRegistro,
  deleteRegistro,
  getRegistroCompleto,
  getPontosVinculados,
  getComissoesVinculadas,
  updatePonto,
  cancelarPonto,
  destravarPonto,
  travaDeEdicaoDisponivel,
  addPontoManual,
  updateComissao,
  cancelarComissao,
  addComissaoManual,
  getGHLSourcesList,
  getGhlPessoaAliasMap,
  getContatosColetados,
  updateContatoColetado,
  deleteContatoColetado,
  getLeadsDesk,
  updateLeadDesk,
  deleteLeadDesk,
  getGhlContatoInfo,
} from '../services/api';
import { ACOES_LEDGER, motivoBloqueioTrocaAcao, planejarEdicaoPonto, escritorAutomaticoDe } from '../services/pontosConfig';
import { getGhlOpcoesVenda } from '../services/ghlIntegracao';
import { getLeadJourney } from '../services/leadJourney';
import { ghlOppUrl, ghlContactUrl } from '../lib/ghlLink';
import { diaSP, hojeSP, horaSP } from '../lib/tz';

// Opções canônicas (listas suspensas — evita texto livre divergente).
const TIPO_AGENDAMENTO_OPCOES = ['Novo Agendamento', 'Reagendamento', 'Agendamento de Transferido', 'Continuação da Agenda'];
const ACAO_PONTO_OPCOES = ACOES_LEDGER;
const TIPO_COMISSAO_OPCOES = ['Agendamento', 'Venda', 'Venda (Pré-venda)', 'Venda (Gestão Comercial)', 'Número Coletado'];

// ── Helpers ───────────────────────────────────────────────────────────────────


const subtractDays = (dateStr, days) => {
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [y, m, d] = String(dateStr).slice(0, 10).split('-');
    if (!y || !m || !d) return '—';
    return `${d}/${m}/${y}`;
};

const formatDateTime = (isoStr) => {
    if (!isoStr) return '—';
    return `${formatDate(diaSP(isoStr))} ${horaSP(isoStr)}`;
};

const formatTime = (isoStr) => {
    if (!isoStr) return '—';
    return horaSP(isoStr);
};

const formatCurrency = (val) =>
    Number(val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Telefone canônico (55 + DDD + número) → exibição legível +55 (DD) 9....-....
const formatPhone = (tel) => {
    const d = String(tel || '').replace(/\D/g, '');
    if (!d) return tel || '—';
    // Parte nacional (DDD + 8/9 dígitos): com país 55 (12/13 díg) ou sem país (10/11 díg).
    let nac = null;
    if (d.startsWith('55') && (d.length === 12 || d.length === 13)) nac = d.slice(2);
    else if (d.length === 10 || d.length === 11) nac = d;
    if (nac) {
        const ddd = nac.slice(0, 2);
        const rest = nac.slice(2);
        const meio = rest.length === 9 ? `${rest.slice(0, 5)}-${rest.slice(5)}` : `${rest.slice(0, 4)}-${rest.slice(4)}`;
        return `+55 (${ddd}) ${meio}`;
    }
    // Portugal (+351 + 9 dígitos): +351 9XX XXX XXX
    if (d.startsWith('351') && d.length === 12) {
        const n = d.slice(3);
        return `+351 ${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
    }
    return `+${d}`; // outro internacional — mostra com "+", sem máscara
};

const matchBusca = (r, termo) => {
    const t = termo.trim().toLowerCase();
    if (!t) return true;
    const nome = (r.nome || '').toLowerCase();
    const email = (r.email || '').toLowerCase();
    if (nome.includes(t) || email.includes(t)) return true;
    const digits = t.replace(/\D/g, '');
    if (digits && (r.telefone || '').replace(/\D/g, '').includes(digits)) return true;
    return false;
};

const CARGO_TO_TIPO = {
    sdr: 'SDR',
    'social seller': 'Social Seller',
    closer: 'Closer',
};

const CHIP_CLASSES = {
    SDR: 'chip chip-blue',
    'Social Seller': 'chip chip-purple',
    Closer: 'chip chip-orange',
};

const TAB_EVENTOS = 'eventos';
const TAB_DIARIOS = 'diarios';

const TIPO_LABEL = { agendamento: 'Agendamento', venda: 'Venda', coletado: 'Nº Coletado', lead_criado: 'Lead criado' };
const TIPO_CHIP = { agendamento: 'chip chip-active', venda: 'chip chip-pending', coletado: 'chip chip-muted', lead_criado: 'chip chip-active' };
const STATUS_VENDA_LABEL = { realizada: 'Realizada', pendente_campos: 'Pendente', perdida: 'Perdida' };

// Campos timestamptz/date normalizados para <input type="date"> (fatiados p/ YYYY-MM-DD)
const DATE_KEYS = ['data_reuniao', 'agendada_em', 'vendido_em'];

const initial = (nome) => (nome || '?').trim().charAt(0).toUpperCase();

// ── Estilos escopados (evita cascata; hover/sticky que inline não faz) ──────────

const scopedCSS = `
.re-central { --re-radius: 12px; }

/* Cabeçalho de comando */
.re-command {
  display: flex; align-items: center; justify-content: space-between;
  gap: 1rem; flex-wrap: wrap; margin-bottom: 1.25rem;
}
.re-command .re-lead { display: flex; align-items: baseline; gap: 0.6rem; flex-wrap: wrap; }
.re-command .re-sub { font-size: 0.78rem; color: var(--muted); }

/* KPIs */
.re-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.75rem; margin-bottom: 1.25rem; }
.re-kpi {
  position: relative; background: var(--canvas-2); border: 1px solid var(--glass-border);
  border-radius: var(--re-radius); padding: 0.9rem 1.05rem 0.9rem 1.15rem; overflow: hidden;
  box-shadow: var(--glass-shadow); transition: transform 0.16s var(--ease-out-quint), border-color 0.16s;
}
.re-kpi::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--emerald); opacity: 0.85; }
.re-kpi:hover { transform: translateY(-1px); border-color: var(--glass-border-em); }
.re-kpi .k-label { font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: var(--dim); }
.re-kpi .k-value { font-family: var(--font-mono); font-weight: 700; letter-spacing: -0.03em; color: var(--ink); font-variant-numeric: tabular-nums; line-height: 1.1; margin-top: 0.28rem; }
.re-kpi .k-sub { font-size: 0.7rem; color: var(--muted); margin-top: 0.2rem; }

/* Tabela operacional */
.re-tablewrap { overflow-x: auto; border-radius: 10px; }
.re-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.re-table thead th {
  position: sticky; top: 0; z-index: 2; background: var(--canvas-elev);
  padding: 0.6rem 0.9rem; text-align: left; color: var(--dim); font-weight: 700;
  font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.08em; white-space: nowrap;
  border-bottom: 1px solid var(--glass-border);
}
.re-table tbody td { padding: 0.62rem 0.9rem; color: var(--ink); vertical-align: middle; font-size: 0.85rem; border-bottom: 1px solid var(--glass-border-soft, rgba(128,128,128,0.08)); }
.re-table tbody tr { transition: background 0.12s; }
.re-table tbody tr:nth-child(even) td { background: color-mix(in srgb, var(--canvas-elev) 45%, transparent); }
.re-table tbody tr:hover td { background: var(--emerald-100); }
.re-num { font-family: var(--font-mono); font-variant-numeric: tabular-nums; }
.re-leadname { font-weight: 600; cursor: pointer; color: var(--ink); border-bottom: 1px dashed transparent; }
.re-leadname:hover { color: var(--emerald); border-bottom-color: var(--emerald); }

/* Avatar */
.re-avatar { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; background: var(--emerald-100); color: var(--emerald); font-size: 0.7rem; font-weight: 700; flex-shrink: 0; }

/* Botões de ação por linha */
.re-actions { display: flex; gap: 0.3rem; justify-content: flex-end; }
.re-iconbtn {
  display: inline-flex; align-items: center; justify-content: center; gap: 0.28rem;
  height: 27px; padding: 0 0.55rem; border-radius: 7px; border: 1px solid var(--glass-border);
  background: var(--canvas-2); color: var(--muted); font-size: 0.72rem; font-weight: 600; cursor: pointer;
  transition: all 0.13s; white-space: nowrap;
}
.re-iconbtn:hover { color: var(--ink); border-color: var(--glass-border-em); background: var(--emerald-100); }
.re-iconbtn.danger:hover { color: var(--danger-soft); border-color: rgba(239,68,68,0.4); background: rgba(239,68,68,0.08); }

/* Modal / painel */
.re-modal { background: var(--canvas-2); border: 1px solid var(--glass-border); border-radius: 14px; box-shadow: var(--ring-shadow, 0 24px 60px rgba(0,0,0,0.35)); width: 92%; max-height: 92vh; overflow: auto; }
.re-modal.narrow { max-width: 620px; }
.re-modal.wide { max-width: 900px; }
.re-modal-head { position: sticky; top: 0; z-index: 3; display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1.15rem 1.5rem; background: var(--canvas-2); border-bottom: 1px solid var(--glass-border); }
.re-modal-title { margin: 0; font-family: var(--font-section); font-size: 1.05rem; font-weight: 700; color: var(--ink); letter-spacing: -0.01em; }
.re-modal-kicker { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: var(--emerald); }
.re-close { background: none; border: 1px solid var(--glass-border); border-radius: 8px; width: 32px; height: 32px; font-size: 1.2rem; line-height: 1; cursor: pointer; color: var(--muted); transition: all 0.13s; }
.re-close:hover { color: var(--ink); border-color: var(--glass-border-em); }

/* Seções do painel */
.re-section { border: 1px solid var(--glass-border); border-radius: 12px; background: var(--canvas); padding: 1.1rem 1.15rem; }
.re-section + .re-section { margin-top: 1rem; }
.re-section-head { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.9rem; }
.re-section-head .h { font-family: var(--font-section); font-size: 0.82rem; font-weight: 700; color: var(--ink); text-transform: uppercase; letter-spacing: 0.06em; }
.re-section-head .badge { font-size: 0.66rem; font-weight: 700; color: var(--emerald); background: var(--emerald-100); border-radius: 999px; padding: 0.1rem 0.5rem; }
.re-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem 0.9rem; }
@media (max-width: 640px) { .re-grid2 { grid-template-columns: 1fr; } }

/* Linhas editáveis de pontos/comissões */
.re-editrow { display: grid; gap: 0.5rem; align-items: end; padding: 0.7rem 0; border-top: 1px solid var(--glass-border-soft, rgba(128,128,128,0.08)); }
.re-editrow:first-of-type { border-top: none; }
.re-editrow .mini-label { font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: var(--dim); margin-bottom: 0.2rem; }
.re-editrow input, .re-editrow select { width: 100%; padding: 0.45rem 0.55rem; background: var(--canvas-elev); border: 1px solid var(--glass-border); border-radius: 7px; color: var(--ink); font-size: 0.82rem; font-family: var(--font-sans); }
.re-editrow .re-num-input { font-family: var(--font-mono); }

/* Trava de edição manual (bd_pontos.travado_em) */
.re-editrow.travada { border-left: 3px solid var(--warn-soft, #f59e0b); padding-left: 0.55rem; background: rgba(245, 158, 11, 0.04); }
/* Migration da trava ainda não aplicada: a linha continua editável, mas sem proteção. */
.re-editrow.sem-trava { border-left: 3px dashed var(--warn-soft, #f59e0b); padding-left: 0.55rem; }
.re-editrow.travada .re-num-input { border-color: rgba(245, 158, 11, 0.45); }
.re-rowmsg { grid-column: 1 / -1; display: flex; align-items: center; flex-wrap: wrap; gap: 0.4rem; font-size: 0.72rem; line-height: 1.35; color: var(--muted); margin-bottom: 0.35rem; }
.re-rowmsg.alerta { color: var(--warn-soft, #f59e0b); }

/* Troca de ação numa linha da ponte GHL (G2) */
.re-editrow.bloqueada { border-left: 3px solid var(--danger-soft, #ef4444); padding-left: 0.55rem; background: rgba(239, 68, 68, 0.05); }
.re-rowmsg.bloqueio { color: var(--danger-soft, #ef4444); align-items: flex-start; }
.re-editrow select.re-campo-bloqueado { border-color: rgba(239, 68, 68, 0.55); background: rgba(239, 68, 68, 0.06); }
.re-linkbtn { background: none; border: none; padding: 0; font: inherit; color: inherit; text-decoration: underline; cursor: pointer; white-space: nowrap; }

/* Timeline jornada */
.re-timeline { display: grid; gap: 0; }
.re-tl-item { position: relative; padding: 0 0 0.9rem 1.35rem; }
.re-tl-item::before { content: ''; position: absolute; left: 4px; top: 4px; bottom: -2px; width: 2px; background: var(--glass-border); }
.re-tl-item:last-child::before { display: none; }
.re-tl-dot { position: absolute; left: 0; top: 3px; width: 10px; height: 10px; border-radius: 50%; background: var(--emerald); box-shadow: 0 0 0 3px var(--emerald-100); }
.re-tl-ev { font-weight: 600; color: var(--ink); font-size: 0.85rem; text-transform: capitalize; }
.re-tl-meta { font-size: 0.76rem; color: var(--muted); margin-top: 0.1rem; }
`;

// ── Modal overlay ───────────────────────────────────────────────────────────────

const ModalOverlay = ({ isOpen, onClose, size = 'narrow', children }) => {
    if (!isOpen) return null;
    return (
        <div
            className="re-central"
            style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'flex-start',
                justifyContent: 'center', zIndex: 1000, overflow: 'auto', padding: '3vh 1rem',
            }}
            onClick={onClose}
        >
            <div className={`re-modal ${size}`} onClick={e => e.stopPropagation()}>
                {children}
            </div>
        </div>
    );
};

const ModalHead = ({ kicker, title, onClose }) => (
    <div className="re-modal-head">
        <div>
            {kicker && <div className="re-modal-kicker">{kicker}</div>}
            <h2 className="re-modal-title">{title}</h2>
        </div>
        <button className="re-close" onClick={onClose} aria-label="Fechar">×</button>
    </div>
);

// ── Link para o card da oportunidade no GHL (helper em ../lib/ghlLink) ───────────
const GhlLink = ({ oppId, contactId, label, className }) => {
    const url = oppId ? ghlOppUrl(oppId) : ghlContactUrl(contactId);
    if (!url) return null;
    const title = oppId ? 'Abrir o card da oportunidade no GHL' : 'Abrir o card do contato no GHL';
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            title={title}
            className={className}
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--info, #3b82f6)', textDecoration: 'none' }}
        >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            {label ? <span>{label}</span> : null}
        </a>
    );
};

// ── Cartão de identidade do lead (cabeçalho da jornada) ──────────────────────────

const IdentidadeCard = ({ j, lead }) => {
    const id = j?.identidade || {};
    const tags = j?.tags || { atuais: [], historico: [] };
    const nome = j?.lead?.full_name || lead?.nome || '—';
    const linhaInfo = [
        id.empresa,
        id.segmento,
        id.faturamento ? `Fat. ${id.faturamento}` : null,
        id.cargo,
    ].filter(Boolean).join(' · ');
    const resp = id.responsaveis || {};
    const temResp = resp.agendou || resp.closer || resp.vendeu;
    const temTags = (tags.atuais?.length || 0) + (tags.historico?.length || 0) > 0;
    return (
        <section className="re-section" style={{ marginBottom: '1rem', borderColor: 'var(--glass-border-em)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem', alignItems: 'baseline' }}>
                <div style={{ fontSize: '1rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                    {nome}
                    <GhlLink oppId={lead?.ghl_opportunity_id || j?.lead?.ghl_opportunity_id} />
                </div>
                {id.fonte && <span className="chip chip-info">{id.fonte}</span>}
                {id.score != null && <span className="chip chip-muted">score {id.score}</span>}
                {id.criado_em && <span style={{ fontSize: '0.76rem', color: 'var(--muted)' }}>Entrou em {formatDate(id.criado_em)}</span>}
            </div>
            {linhaInfo && <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.3rem' }}>{linhaInfo}</div>}
            {temResp && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.5rem' }}>
                    {resp.agendou && <span className="chip chip-blue">Agendou: {resp.agendou}</span>}
                    {resp.closer && <span className="chip chip-orange">Closer: {resp.closer}</span>}
                    {resp.vendeu && <span className="chip chip-active">Vendeu: {resp.vendeu}</span>}
                </div>
            )}
            {temTags && (
                <div style={{ marginTop: '0.6rem', paddingTop: '0.5rem', borderTop: '1px solid var(--glass-border)' }}>
                    <div style={{ fontSize: '0.6rem', color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.3rem' }}>Tags do CRM</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {(tags.atuais || []).map((t, i) => <span key={`a${i}`} className="chip chip-purple">{t}</span>)}
                        {(tags.historico || []).map((t, i) => <span key={`h${i}`} className="chip chip-muted" style={{ opacity: 0.55 }}>{t}</span>)}
                    </div>
                </div>
            )}
        </section>
    );
};

// ── Pop-up de jornada ────────────────────────────────────────────────────────────

const JornadaPopup = ({ isOpen, onClose, lead, telefone }) => {
    const [journey, setJourney] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        // Reset de estado ao abrir o modal, antes de buscar a jornada. Mesmo padrão de busca
        // assíncrona do resto da tela — ver nota em RegistroEnvios.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setLoading(true);
        setError('');
        setJourney(null);
        getLeadJourney({ ghl_opportunity_id: lead?.ghl_opportunity_id, telefone })
            .then(data => setJourney(data))
            .catch(err => setError(err?.message || 'Erro ao carregar jornada.'))
            .finally(() => setLoading(false));
    }, [isOpen, lead, telefone]);

    const j = journey;
    return (
        <ModalOverlay isOpen={isOpen} onClose={onClose} size="wide">
            <ModalHead kicker="Jornada do lead" title={lead?.nome || j?.lead?.full_name || 'Lead'} onClose={onClose} />
            <div style={{ padding: '1.35rem 1.5rem' }}>
                {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
                {loading ? (
                    <div className="loading-state">Carregando jornada…</div>
                ) : j ? (
                    <>
                    <IdentidadeCard j={j} lead={lead} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        {/* Coluna esquerda */}
                        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
                            <section className="re-section">
                                <div className="re-section-head"><span className="h">Contato</span></div>
                                <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>{j.lead?.full_name || lead?.nome || '—'}</div>
                                <div style={{ fontSize: '0.82rem', color: 'var(--muted)', marginTop: '0.2rem' }}>{formatPhone(telefone || j.lead?.phone_e164)}</div>
                                {j.lead?.email && <div style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>{j.lead.email}</div>}
                                {j.lead?.segmento && <div style={{ fontSize: '0.82rem', color: 'var(--muted)', marginTop: '0.3rem' }}>Segmento: {j.lead.segmento}</div>}
                                {j.lead?.faturamento && <div style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>Faturamento: {j.lead.faturamento}</div>}
                            </section>

                            {j.origem && (j.origem.fonte || j.origem.campaign_name || j.origem.utm_campaign) && (
                                <section className="re-section">
                                    <div className="re-section-head"><span className="h">Origem & Campanha</span></div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.5rem' }}>
                                        {j.origem.fonte && <span className="chip chip-info">{j.origem.fonte}</span>}
                                        {(j.origem.campaign_name || j.origem.utm_campaign) && <span className="chip chip-purple">{j.origem.campaign_name || j.origem.utm_campaign}</span>}
                                    </div>
                                    {j.origem.ad_name && <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Anúncio: {j.origem.ad_name}</div>}
                                    {j.origem.form_name && <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Formulário: {j.origem.form_name}</div>}
                                    {j.origem.utm_medium && <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>utm_medium: {j.origem.utm_medium}</div>}
                                </section>
                            )}

                            <section className="re-section">
                                <div className="re-section-head"><span className="h">Funil</span></div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                                    <div>
                                        <div className="k-label" style={{ fontSize: '0.6rem', color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Passagens</div>
                                        <div className="re-num" style={{ fontSize: '1.1rem', fontWeight: 700 }}>{j.passagens?.pessoas ?? '—'}</div>
                                        <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>pessoa(s)</div>
                                    </div>
                                    <div>
                                        <div className="k-label" style={{ fontSize: '0.6rem', color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Agendamentos</div>
                                        <div className="re-num" style={{ fontSize: '1.1rem', fontWeight: 700 }}>{j.passagens?.agendamentos ?? 0}</div>
                                    </div>
                                    <div>
                                        <div className="k-label" style={{ fontSize: '0.6rem', color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Até agendar</div>
                                        <div className="re-num" style={{ fontSize: '1.1rem', fontWeight: 700 }}>{j.tempos?.criacaoAteAgendamento != null ? `${j.tempos.criacaoAteAgendamento}d` : '—'}</div>
                                    </div>
                                    <div>
                                        <div className="k-label" style={{ fontSize: '0.6rem', color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Até vender</div>
                                        <div className="re-num" style={{ fontSize: '1.1rem', fontWeight: 700 }}>{j.tempos?.agendamentoAteVenda != null ? `${j.tempos.agendamentoAteVenda}d` : '—'}</div>
                                    </div>
                                </div>
                                {j.custo != null && (
                                    <div style={{ marginTop: '0.9rem', paddingTop: '0.75rem', borderTop: '1px solid var(--glass-border)' }}>
                                        <div className="k-label" style={{ fontSize: '0.6rem', color: 'var(--dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Custo acumulado do lead</div>
                                        <div className="re-num" style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--warn-soft)', marginTop: '0.15rem' }}>{formatCurrency(j.custo)}</div>
                                    </div>
                                )}
                            </section>
                        </div>

                        {/* Coluna direita */}
                        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
                            {j.timeline && j.timeline.length > 0 && (
                                <section className="re-section">
                                    <div className="re-section-head"><span className="h">Linha do tempo</span></div>
                                    <div className="re-timeline">
                                        {j.timeline.map((evt, i) => (
                                            <div className="re-tl-item" key={i}>
                                                <span className="re-tl-dot" />
                                                <div className="re-tl-ev">{String(evt.evento || '').replace(/_/g, ' ')}</div>
                                                <div className="re-tl-meta">{evt.pessoa || '—'} · {formatDateTime(evt.quando)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {j.reunioes && j.reunioes.length > 0 && (
                                <section className="re-section">
                                    <div className="re-section-head"><span className="h">Reuniões</span><span className="badge">{j.reunioes.length}</span></div>
                                    <div style={{ display: 'grid', gap: '0.6rem' }}>
                                        {j.reunioes.map((r, i) => (
                                            <div key={i} style={{ padding: '0.7rem 0.8rem', background: 'var(--canvas-elev)', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                                                    <strong style={{ fontSize: '0.82rem' }}>R{r.seq} · {r.tipo_reuniao || r.tipo_agendamento || '—'}</strong>
                                                    <span className={r.comparecimento === true ? 'chip chip-active' : r.comparecimento === false ? 'chip chip-danger' : 'chip chip-muted'}>
                                                        {r.comparecimento === true ? 'Compareceu' : r.comparecimento === false ? 'Faltou' : 'Pendente'}
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: '0.76rem', color: 'var(--muted)', marginTop: '0.3rem' }}>
                                                    {formatDate(r.data_reuniao)} {r.hora_reuniao || ''} · SDR {r.sdr || '—'} · Closer {r.closer || '—'}
                                                </div>
                                                {r.briefing && <div style={{ fontSize: '0.76rem', color: 'var(--muted)', marginTop: '0.35rem', fontStyle: 'italic' }}>“{r.briefing}”</div>}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {j.venda && (
                                <section className="re-section" style={{ borderColor: 'var(--glass-border-em)' }}>
                                    <div className="re-section-head"><span className="h">Venda</span></div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <span className="re-num" style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--emerald)' }}>{formatCurrency(j.venda.valor_venda)}</span>
                                        <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{j.venda.produto || '—'}</span>
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.35rem' }}>
                                        {j.venda.forma_pagamento || '—'}{j.venda.parcelas ? ` · ${j.venda.parcelas}x` : ''} · {formatDate(j.venda.vendido_em)}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Closer {j.venda.closer || '—'} · SDR {j.venda.sdr || '—'}</div>
                                    {j.venda.receita && j.venda.receita.length > 0 && (
                                        <div style={{ marginTop: '0.6rem', paddingTop: '0.5rem', borderTop: '1px solid var(--glass-border)' }}>
                                            {j.venda.receita.map((p, i) => (
                                                <div key={i} className="re-num" style={{ fontSize: '0.76rem', color: 'var(--muted)' }}>
                                                    {p.parcela}: {formatCurrency(p.valor)} · {p.forma_pagamento || '—'} · {formatDate(p.data_venda)}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {j.venda.briefing && <div style={{ fontSize: '0.76rem', color: 'var(--muted)', marginTop: '0.4rem', fontStyle: 'italic' }}>“{j.venda.briefing}”</div>}
                                </section>
                            )}
                        </div>
                    </div>
                    </>
                ) : (
                    <div className="empty-state"><div className="empty-state-title">Nenhum dado de jornada encontrado.</div></div>
                )}
            </div>
        </ModalOverlay>
    );
};

// ── Linha editável de PONTO ───────────────────────────────────────────────────────

// Exportada para `PontoRow.test.jsx` renderizar a linha de verdade (jsdom): o bloqueio da troca
// de ação e o estado "travado" são promessas feitas na TELA, e promessa de tela só se verifica
// renderizando. O resto do arquivo continua sem export — não é uma API, é um ponto de teste.
export function PontoRow({ ponto, pessoas, onChanged, onError }) {
    const [pessoa, setPessoa] = useState(ponto.pessoa || '');
    const [pontos, setPontos] = useState(ponto.pontos ?? '');
    const [acao, setAcao] = useState(ponto.acao || '');
    const [data, setData] = useState((ponto.data || '').slice(0, 10));
    const [busy, setBusy] = useState(false);

    const dirty = pessoa !== (ponto.pessoa || '') || String(pontos) !== String(ponto.pontos ?? '') ||
        acao !== (ponto.acao || '') || data !== (ponto.data || '').slice(0, 10);

    const travado = !!ponto.travado_em;
    // A coluna `travado_em` só existe depois da migration 2026-08-25, que é ato manual do dono —
    // e o desk deploya por push. Nessa janela a edição funciona sem carimbo, e a linha tem que
    // dizer isso: prometer "não é mais sobrescrito" com o banco sem a coluna é a mentira que faz o
    // operador editar, sair da tela e perder o valor em 1 min sem entender por quê.
    const semTrava = travaDeEdicaoDisponivel() === false;
    // A decisão vive em `pontosConfig` e não aqui: assim o bloqueio da troca de ação é
    // verificável sem renderizar a tela (pontos-trava.test.js).
    const bloqueio = motivoBloqueioTrocaAcao(ponto);
    const trocandoAcao = !!bloqueio && acao !== (ponto.acao || '');
    // Só a ponte GHL reescreve valor a cada ciclo. Nas demais linhas a trava é só autoria, e
    // prometer "para de sobrescrever" seria mentir sobre um sweep que não passa por elas.
    const sobSweep = !!escritorAutomaticoDe(ponto)?.reescreve;

    const salvar = async () => {
        const p = pessoas.find(x => x.nome === pessoa);
        const plano = planejarEdicaoPonto(ponto, {
            pessoa,
            pessoa_id: p?.id ?? ponto.pessoa_id,
            cargo: p?.cargo ?? ponto.cargo,
            pontos, acao, data,
        });
        if (!plano.ok) { onError(plano.motivo); return; }
        setBusy(true);
        try {
            await updatePonto({ id: ponto.id, patch: plano.patch });
            await onChanged();
        } catch (e) { onError(e?.message || 'Erro ao salvar ponto.'); } finally { setBusy(false); }
    };

    const destravar = async () => {
        const aviso = sobSweep
            ? `Destravar o ponto de ${ponto.pessoa}? O cálculo automático volta a mandar nesta linha e pode sobrescrever ou apagar o valor ${ponto.pontos} no próximo ciclo (1 min).`
            : `Destravar o ponto de ${ponto.pessoa}? Some só a marca de edição manual — nenhum cálculo automático reescreve esta linha, o valor ${ponto.pontos} continua o mesmo.`;
        if (!confirm(aviso)) return;
        setBusy(true);
        try { await destravarPonto({ id: ponto.id }); await onChanged(); }
        catch (e) { onError(e?.message || 'Erro ao destravar ponto.'); } finally { setBusy(false); }
    };

    const cancelar = async () => {
        if (!confirm(`Cancelar o ponto de ${ponto.pessoa} (${ponto.pontos})? Sai do ranking.`)) return;
        setBusy(true);
        try { await cancelarPonto({ id: ponto.id }); await onChanged(); }
        catch (e) { onError(e?.message || 'Erro ao cancelar ponto.'); } finally { setBusy(false); }
    };

    return (
        <div className={`re-editrow${travado ? ' travada' : ''}${semTrava ? ' sem-trava' : ''}${trocandoAcao ? ' bloqueada' : ''}`} style={{ gridTemplateColumns: '2fr 0.8fr 1.2fr 1.2fr auto' }}>
            {semTrava ? (
                <div className="re-rowmsg alerta">
                    <span className="chip chip-warn">⚠ Sem trava</span>
                    <span>
                        O banco ainda não tem a coluna <code>travado_em</code> — falta rodar
                        {' '}<code>supabase/migration-2026-08-25-pontos-trava-manual.sql</code>.
                        {sobSweep
                            ? ' Salvar grava o valor, mas o cálculo automático o sobrescreve no próximo ciclo (1 min). Avise quem cuida do banco antes de editar.'
                            : ' Salvar grava o valor; a linha só fica sem registro de quem editou.'}
                    </span>
                </div>
            ) : travado ? (
                <div className="re-rowmsg">
                    <span className="chip chip-warn">🔒 Travado</span>
                    <span>
                        Editado em {formatDateTime(ponto.travado_em)}{ponto.travado_por ? ` por ${ponto.travado_por}` : ''}.
                        {sobSweep
                            ? ' O cálculo automático não sobrescreve mais este valor.'
                            : ' Nenhum cálculo automático reescreve esta linha; a marca registra quem editou.'}
                    </span>
                </div>
            ) : dirty && (
                <div className="re-rowmsg alerta">
                    <span className="chip chip-muted">{sobSweep ? '🔓 Automático' : '✎ Manual'}</span>
                    <span>
                        {sobSweep
                            ? 'Salvar trava esta linha: o valor passa a ser o seu e o cálculo automático para de sobrescrevê-lo.'
                            : 'Salvar marca a linha como editada à mão, com data e autor. O valor já não era recalculado por ninguém.'}
                    </span>
                </div>
            )}
            {/* Aviso da troca de ação: fica sempre visível na linha da ponte, não só num `title`
                de campo desabilitado — campo desabilitado não dispara hover e o tooltip nunca
                aparece. Ao divergir a ação, o aviso vira o motivo completo e o Salvar trava. */}
            {bloqueio && (
                <div className={`re-rowmsg${trocandoAcao ? ' bloqueio' : ''}`}>
                    <span className={`chip ${trocandoAcao ? 'chip-danger' : 'chip-muted'}`}>
                        {trocandoAcao ? '⛔ Troca bloqueada' : '🔗 Ação fixa'}
                    </span>
                    <span>{trocandoAcao ? `${bloqueio.resumo} ${bloqueio.detalhe}` : bloqueio.resumo}</span>
                    {trocandoAcao && (
                        <button type="button" className="re-linkbtn" onClick={() => setAcao(ponto.acao || '')}>
                            Voltar para “{bloqueio.acao}”
                        </button>
                    )}
                </div>
            )}
            {/* Os `mini-label` são <div>, não <label>: não nomeiam o campo para leitor de tela
                nem para teste. `aria-label` dá nome a cada controle da linha. */}
            <div>
                <div className="mini-label">Pra quem</div>
                <select aria-label="Pra quem" value={pessoa} onChange={e => setPessoa(e.target.value)}>
                    <option value="">—</option>
                    {pessoas.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                </select>
            </div>
            <div>
                <div className="mini-label">Pontos</div>
                <input aria-label="Pontos" className="re-num-input" type="number" step="0.5" value={pontos} onChange={e => setPontos(e.target.value)} />
            </div>
            <div>
                <div className="mini-label">Ação</div>
                <select
                    aria-label="Ação"
                    className={trocandoAcao ? 're-campo-bloqueado' : undefined}
                    value={acao}
                    onChange={e => setAcao(e.target.value)}
                >
                    {!ACAO_PONTO_OPCOES.includes(acao) && acao && <option value={acao}>{acao}</option>}
                    {ACAO_PONTO_OPCOES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            </div>
            <div>
                <div className="mini-label">Data</div>
                <input aria-label="Data" type="date" value={data} onChange={e => setData(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '0.3rem', paddingBottom: '0.05rem' }}>
                <button
                    className="re-iconbtn"
                    disabled={!dirty || busy || trocandoAcao}
                    onClick={salvar}
                    title={trocandoAcao ? bloqueio.resumo : 'Salvar'}
                >Salvar</button>
                {travado && !semTrava && <button className="re-iconbtn" disabled={busy} onClick={destravar} title="Destravar: devolve a linha ao cálculo automático">Destravar</button>}
                <button className="re-iconbtn danger" disabled={busy} onClick={cancelar} title="Cancelar (soft-delete)">✕</button>
            </div>
        </div>
    );
}

function PontoAddRow({ pessoas, tabela, referenciaId, onChanged, onError }) {
    const [pessoa, setPessoa] = useState('');
    const [pontos, setPontos] = useState('');
    const [acao, setAcao] = useState('manual');
    const [data, setData] = useState(hojeSP());
    const [busy, setBusy] = useState(false);

    const adicionar = async () => {
        if (!pessoa || pontos === '') { onError('Informe pessoa e pontos.'); return; }
        setBusy(true);
        try {
            const p = pessoas.find(x => x.nome === pessoa);
            await addPontoManual({
                tabela, referencia_id: referenciaId, pessoa,
                pessoa_id: p?.id, cargo: p?.cargo, acao, pontos: Number(pontos), data,
            });
            setPessoa(''); setPontos(''); setAcao('manual'); setData(hojeSP());
            await onChanged();
        } catch (e) { onError(e?.message || 'Erro ao adicionar ponto.'); } finally { setBusy(false); }
    };

    return (
        <div className="re-editrow" style={{ gridTemplateColumns: '2fr 0.8fr 1.2fr 1.2fr auto', borderTop: '1px dashed var(--glass-border)' }}>
            <div>
                <div className="mini-label">+ Pra quem</div>
                <select value={pessoa} onChange={e => setPessoa(e.target.value)}>
                    <option value="">—</option>
                    {pessoas.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                </select>
            </div>
            <div>
                <div className="mini-label">Pontos</div>
                <input className="re-num-input" type="number" step="0.5" value={pontos} onChange={e => setPontos(e.target.value)} />
            </div>
            <div>
                <div className="mini-label">Ação</div>
                <select value={acao} onChange={e => setAcao(e.target.value)}>
                    {!ACAO_PONTO_OPCOES.includes(acao) && acao && <option value={acao}>{acao}</option>}
                    {ACAO_PONTO_OPCOES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            </div>
            <div>
                <div className="mini-label">Data</div>
                <input type="date" value={data} onChange={e => setData(e.target.value)} />
            </div>
            <div style={{ display: 'flex', paddingBottom: '0.05rem' }}>
                <button className="re-iconbtn" disabled={busy} onClick={adicionar} title="Adicionar ponto manual">+ Adicionar</button>
            </div>
        </div>
    );
}

// ── Linha editável de COMISSÃO ────────────────────────────────────────────────────

function ComissaoRow({ comissao, pessoas, onChanged, onError }) {
    const [pessoa, setPessoa] = useState(comissao.pessoa || '');
    const [ganho, setGanho] = useState(comissao.ganho ?? '');
    const [tipo, setTipo] = useState(comissao.tipo || '');
    const [data, setData] = useState((comissao.data_base || '').slice(0, 10));
    const [busy, setBusy] = useState(false);

    const dirty = pessoa !== (comissao.pessoa || '') || String(ganho) !== String(comissao.ganho ?? '') ||
        tipo !== (comissao.tipo || '') || data !== (comissao.data_base || '').slice(0, 10);

    const salvar = async () => {
        setBusy(true);
        try {
            const p = pessoas.find(x => x.nome === pessoa);
            await updateComissao({ id: comissao.id, patch: {
                pessoa,
                pessoa_id: p?.id ?? comissao.pessoa_id,
                cargo: p?.cargo ?? comissao.cargo,
                ganho: ganho === '' ? 0 : Number(ganho),
                tipo,
                data_base: data,
            } });
            await onChanged();
        } catch (e) { onError(e?.message || 'Erro ao salvar comissão.'); } finally { setBusy(false); }
    };

    const cancelar = async () => {
        if (!confirm(`Cancelar a comissão de ${comissao.pessoa} (${formatCurrency(comissao.ganho)})? Sai do pagamento.`)) return;
        setBusy(true);
        try { await cancelarComissao({ id: comissao.id }); await onChanged(); }
        catch (e) { onError(e?.message || 'Erro ao cancelar comissão.'); } finally { setBusy(false); }
    };

    return (
        <div className="re-editrow" style={{ gridTemplateColumns: '2fr 1fr 1.2fr 1.2fr auto' }}>
            <div>
                <div className="mini-label">Pra quem</div>
                <select value={pessoa} onChange={e => setPessoa(e.target.value)}>
                    <option value="">—</option>
                    {pessoas.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                </select>
            </div>
            <div>
                <div className="mini-label">Ganho (R$)</div>
                <input className="re-num-input" type="number" step="0.01" value={ganho} onChange={e => setGanho(e.target.value)} />
            </div>
            <div>
                <div className="mini-label">Tipo</div>
                <select value={tipo} onChange={e => setTipo(e.target.value)}>
                    {!TIPO_COMISSAO_OPCOES.includes(tipo) && tipo && <option value={tipo}>{tipo}</option>}
                    {TIPO_COMISSAO_OPCOES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            </div>
            <div>
                <div className="mini-label">Data base</div>
                <input type="date" value={data} onChange={e => setData(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '0.3rem', paddingBottom: '0.05rem' }}>
                <button className="re-iconbtn" disabled={!dirty || busy} onClick={salvar} title="Salvar">Salvar</button>
                <button className="re-iconbtn danger" disabled={busy} onClick={cancelar} title="Cancelar (soft-delete)">✕</button>
            </div>
        </div>
    );
}

function ComissaoAddRow({ pessoas, telefone, dataBase, tabela, referenciaId, onChanged, onError }) {
    const [pessoa, setPessoa] = useState('');
    const [ganho, setGanho] = useState('');
    const [tipo, setTipo] = useState('Agendamento');
    const [data, setData] = useState(dataBase || hojeSP());
    const [busy, setBusy] = useState(false);

    const adicionar = async () => {
        if (!pessoa || ganho === '') { onError('Informe pessoa e ganho.'); return; }
        setBusy(true);
        try {
            const p = pessoas.find(x => x.nome === pessoa);
            await addComissaoManual({ telefone, data_base: data, pessoa, pessoa_id: p?.id, cargo: p?.cargo, tipo, ganho: Number(ganho), referencia_tabela: tabela, referencia_id: referenciaId });
            setPessoa(''); setGanho(''); setTipo('Agendamento'); setData(dataBase || hojeSP());
            await onChanged();
        } catch (e) { onError(e?.message || 'Erro ao adicionar comissão.'); } finally { setBusy(false); }
    };

    return (
        <div className="re-editrow" style={{ gridTemplateColumns: '2fr 1fr 1.2fr 1.2fr auto', borderTop: '1px dashed var(--glass-border)' }}>
            <div>
                <div className="mini-label">+ Pra quem</div>
                <select value={pessoa} onChange={e => setPessoa(e.target.value)}>
                    <option value="">—</option>
                    {pessoas.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                </select>
            </div>
            <div>
                <div className="mini-label">Ganho (R$)</div>
                <input className="re-num-input" type="number" step="0.01" value={ganho} onChange={e => setGanho(e.target.value)} />
            </div>
            <div>
                <div className="mini-label">Tipo</div>
                <select value={tipo} onChange={e => setTipo(e.target.value)}>
                    {TIPO_COMISSAO_OPCOES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            </div>
            <div>
                <div className="mini-label">Data base</div>
                <input type="date" value={data} onChange={e => setData(e.target.value)} />
            </div>
            <div style={{ display: 'flex', paddingBottom: '0.05rem' }}>
                <button className="re-iconbtn" disabled={busy} onClick={adicionar} title="Adicionar comissão">+ Adicionar</button>
            </div>
        </div>
    );
}

// ── Modal de edição (painel de controle TOTAL) ──────────────────────────────────

const EditModal = ({ isOpen, onClose, item, tabela, onSave }) => {
    const isReuniao = tabela === 'ghl_reunioes';
    const [baseline, setBaseline] = useState({});
    const [formData, setFormData] = useState({});
    const [pessoas, setPessoas] = useState([]);
    const [pontos, setPontos] = useState([]);
    const [comissoes, setComissoes] = useState([]);
    const [fontes, setFontes] = useState([]);
    const [opcoesVenda, setOpcoesVenda] = useState({ produtos: [], formasPagamento: [] });
    const [loadingSub, setLoadingSub] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Data do evento (para escopar comissões e sugerir data em novas): data do agendamento
    // (reunião) ou data da venda. Comissões só aparecem/são criadas para ESTE evento.
    const eventoData = isReuniao ? (item?.data_agendamento || null) : (item?.data_venda || null);

    const recarregarVinculos = useCallback(async () => {
        if (!item) return;
        const data = tabela === 'ghl_reunioes' ? item.data_agendamento : item.data_venda;
        try {
            const [pts, coms] = await Promise.all([
                getPontosVinculados({ tabela, referencia_id: item.id }),
                getComissoesVinculadas({ telefone: item.telefone, data, tabela, referencia_id: item.id }),
            ]);
            setPontos(pts);
            setComissoes(coms);
        } catch (e) {
            setError(e?.message || 'Erro ao carregar pontos/comissões.');
        }
    }, [item, tabela]);

    useEffect(() => {
        if (!isOpen || !item) return;
        let cancel = false;
        setError('');
        setLoadingSub(true);
        setBaseline({});
        setFormData({});
        getTodasPessoas().then(p => { if (!cancel) setPessoas(p); }).catch(() => {});
        (async () => {
            try {
                const [raw, alias, srcs] = await Promise.all([
                    getRegistroCompleto({ tabela, id: item.id }),
                    getGhlPessoaAliasMap().catch(() => new Map()),
                    getGHLSourcesList().catch(() => []),
                ]);
                if (cancel) return;
                setFontes(Array.isArray(srcs) ? srcs : []);
                const norm = { ...(raw || {}) };
                DATE_KEYS.forEach(k => { if (norm[k]) norm[k] = String(norm[k]).slice(0, 10); });
                if (norm.hora_reuniao) norm.hora_reuniao = String(norm.hora_reuniao).slice(0, 5);
                // Resolve SDR/closer pelo alias quando o campo cru vier vazio: o nome aparece
                // na lista via ghl_pessoa_alias, mas o registro cru às vezes só tem o ghl_user_id.
                norm.sdr_nome = norm.sdr_nome || (norm.sdr_ghl_user_id ? (alias.get(norm.sdr_ghl_user_id) || '') : '');
                norm.closer_nome = norm.closer_nome || (norm.closer_ghl_user_id ? (alias.get(norm.closer_ghl_user_id) || '') : '');
                setBaseline(norm);
                setFormData(norm);
            } catch (e) {
                if (!cancel) setError(e?.message || 'Erro ao carregar registro.');
            }
            await recarregarVinculos();
            if (!cancel) setLoadingSub(false);
        })();
        return () => { cancel = true; };
    }, [isOpen, item, tabela, recarregarVinculos]);

    useEffect(() => {
        if (!isOpen || isReuniao) return;
        getGhlOpcoesVenda().then(setOpcoesVenda).catch(() => {});
    }, [isOpen, isReuniao]);

    const setPessoaField = (papel, nome) => {
        const p = pessoas.find(x => x.nome === nome);
        setFormData(fd => ({
            ...fd,
            [`${papel}_nome`]: nome,
            [`${papel}_ghl_user_id`]: p?.ghl_user_id ?? null,
        }));
    };

    const salvarDados = async () => {
        setSaving(true);
        setError('');
        try {
            const whitelist = isReuniao
                ? ['data_reuniao', 'hora_reuniao', 'agendada_em', 'sdr_nome', 'sdr_ghl_user_id',
                   'closer_nome', 'closer_ghl_user_id', 'tipo_agendamento', 'tipo_reuniao', 'reuniao_completa', 'fonte',
                   'comparecimento', 'campos_pendentes', 'descricao']
                : ['valor_venda', 'produto', 'forma_pagamento', 'status', 'vendido_em',
                   'closer_nome', 'closer_ghl_user_id', 'sdr_nome', 'sdr_ghl_user_id', 'parcelas', 'descricao'];
            const patch = {};
            whitelist.forEach(k => {
                if (k in formData && formData[k] !== baseline[k]) patch[k] = formData[k];
            });
            if (Object.keys(patch).length > 0) {
                await updateRegistro({ tabela, id: item.id, patch });
            }
            await onSave();
            onClose();
        } catch (e) {
            setError(e?.message || 'Erro ao salvar.');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !item) return null;

    const val = (k) => (formData[k] ?? '');
    const dateVal = (k) => (formData[k] ? String(formData[k]).slice(0, 10) : '');

    return (
        <ModalOverlay isOpen={isOpen} onClose={onClose} size="wide">
            <ModalHead
                kicker={isReuniao ? 'Editar agendamento / reunião' : 'Editar venda'}
                title={item.nome || baseline.contato_nome || 'Registro'}
                onClose={onClose}
            />
            <div style={{ padding: '1.3rem 1.5rem', display: 'grid', gap: '1rem' }}>
                {error && <div className="alert alert-error">{error}</div>}

                {/* ── Dados do registro ── */}
                <section className="re-section">
                    <div className="re-section-head"><span className="h">Dados do registro</span></div>
                    <div className="re-grid2">
                        {isReuniao ? (
                            <>
                                <FormGroup label="Data da reunião" htmlFor="ed-data-r">
                                    <Input id="ed-data-r" type="date" value={dateVal('data_reuniao')} onChange={e => setFormData({ ...formData, data_reuniao: e.target.value })} />
                                </FormGroup>
                                <FormGroup label="Hora da reunião" htmlFor="ed-hora-r">
                                    <Input id="ed-hora-r" type="time" value={val('hora_reuniao')} onChange={e => setFormData({ ...formData, hora_reuniao: e.target.value })} />
                                </FormGroup>
                                <FormGroup label="Data do agendamento" htmlFor="ed-data-ag">
                                    <Input id="ed-data-ag" type="date" value={dateVal('agendada_em')} onChange={e => setFormData({ ...formData, agendada_em: e.target.value })} />
                                </FormGroup>
                                <FormGroup label="SDR (quem agendou)" htmlFor="ed-sdr">
                                    <Select id="ed-sdr" value={val('sdr_nome')} onChange={e => setPessoaField('sdr', e.target.value)}>
                                        <option value="">—</option>
                                        {pessoas.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Closer" htmlFor="ed-closer">
                                    <Select id="ed-closer" value={val('closer_nome')} onChange={e => setPessoaField('closer', e.target.value)}>
                                        <option value="">—</option>
                                        {pessoas.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Tipo de agendamento" htmlFor="ed-tipoag">
                                    <Select id="ed-tipoag" value={val('tipo_agendamento')} onChange={e => setFormData({ ...formData, tipo_agendamento: e.target.value })}>
                                        <option value="">—</option>
                                        {formData.tipo_agendamento && !TIPO_AGENDAMENTO_OPCOES.includes(formData.tipo_agendamento) && <option value={formData.tipo_agendamento}>{formData.tipo_agendamento}</option>}
                                        {TIPO_AGENDAMENTO_OPCOES.map(o => <option key={o} value={o}>{o}</option>)}
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Tipo de reunião" htmlFor="ed-tiporeu">
                                    {/* "Completa" não é valor de tipo_reuniao (etapa do GHL, lida pelo projetor e pelas
                                        notificações): é R1 de qualificação + reuniao_completa=true, que o dashboard
                                        converte em "Reunião Completa" (legacy-adapter.ts → tipoReuniaoGhl). */}
                                    <Select id="ed-tiporeu" value={formData.reuniao_completa === true ? 'completa' : val('tipo_reuniao')} onChange={e => {
                                        const v = e.target.value;
                                        setFormData({
                                            ...formData,
                                            tipo_reuniao: v === 'completa' ? 'qualificacao' : v,
                                            reuniao_completa: v === 'completa' ? true : (formData.reuniao_completa === true ? false : formData.reuniao_completa),
                                        });
                                    }}>
                                        <option value="">—</option>
                                        <option value="qualificacao">Qualificação (R1)</option>
                                        <option value="completa">Reunião Completa (R1)</option>
                                        <option value="apresentacao">Apresentação (R2)</option>
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Fonte" htmlFor="ed-fonte">
                                    <Select id="ed-fonte" value={val('fonte')} onChange={e => setFormData({ ...formData, fonte: e.target.value })}>
                                        <option value="">—</option>
                                        {formData.fonte && !fontes.includes(formData.fonte) && <option value={formData.fonte}>{formData.fonte}</option>}
                                        {fontes.map(o => <option key={o} value={o}>{o}</option>)}
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Comparecimento" htmlFor="ed-comp">
                                    <Select id="ed-comp" value={String(formData.comparecimento ?? '')} onChange={e => setFormData({ ...formData, comparecimento: e.target.value === '' ? null : e.target.value === 'true' })}>
                                        <option value="">Pendente / N/A</option>
                                        <option value="true">Compareceu</option>
                                        <option value="false">Faltou</option>
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Campos pendentes" htmlFor="ed-pend">
                                    <Select id="ed-pend" value={String(formData.campos_pendentes ?? 'false')} onChange={e => setFormData({ ...formData, campos_pendentes: e.target.value === 'true' })}>
                                        <option value="false">Não (completo)</option>
                                        <option value="true">Sim (pendente)</option>
                                    </Select>
                                </FormGroup>
                            </>
                        ) : (
                            <>
                                <FormGroup label="Valor da venda (R$)" htmlFor="ed-valor">
                                    <Input id="ed-valor" type="number" step="0.01" value={val('valor_venda')} onChange={e => setFormData({ ...formData, valor_venda: e.target.value })} />
                                </FormGroup>
                                <FormGroup label="Data da venda" htmlFor="ed-datav">
                                    <Input id="ed-datav" type="date" value={dateVal('vendido_em')} onChange={e => setFormData({ ...formData, vendido_em: e.target.value })} />
                                </FormGroup>
                                <FormGroup label="Produto" htmlFor="ed-produto">
                                    <Select id="ed-produto" value={val('produto')} onChange={e => setFormData({ ...formData, produto: e.target.value })}>
                                        <option value="">—</option>
                                        {[...new Set([...opcoesVenda.produtos, val('produto')].filter(Boolean))].map(o => <option key={o} value={o}>{o}</option>)}
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Forma de pagamento" htmlFor="ed-forma">
                                    <Select id="ed-forma" value={val('forma_pagamento')} onChange={e => setFormData({ ...formData, forma_pagamento: e.target.value })}>
                                        <option value="">—</option>
                                        {[...new Set([...opcoesVenda.formasPagamento, val('forma_pagamento')].filter(Boolean))].map(o => <option key={o} value={o}>{o}</option>)}
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Parcelas" htmlFor="ed-parc">
                                    <Select id="ed-parc" value={val('parcelas')} onChange={e => setFormData({ ...formData, parcelas: e.target.value })}>
                                        <option value="">—</option>
                                        {Array.from({ length: 12 }, (_, i) => String(i + 1)).map(n => <option key={n} value={n}>{n}x</option>)}
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Status" htmlFor="ed-status">
                                    <Select id="ed-status" value={val('status')} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                        <option value="">—</option>
                                        {[...new Set(['realizada', 'pendente_campos', 'perdida', val('status')].filter(Boolean))].map(o => <option key={o} value={o}>{STATUS_VENDA_LABEL[o] || o}</option>)}
                                    </Select>
                                </FormGroup>
                                <FormGroup label="Closer (vendedor)" htmlFor="ed-closerv">
                                    <Select id="ed-closerv" value={val('closer_nome')} onChange={e => setPessoaField('closer', e.target.value)}>
                                        <option value="">—</option>
                                        {pessoas.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                                    </Select>
                                </FormGroup>
                                <FormGroup label="SDR (pré-vendedor)" htmlFor="ed-sdrv">
                                    <Select id="ed-sdrv" value={val('sdr_nome')} onChange={e => setPessoaField('sdr', e.target.value)}>
                                        <option value="">—</option>
                                        {pessoas.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
                                    </Select>
                                </FormGroup>
                            </>
                        )}
                    </div>
                    <div style={{ marginTop: '0.75rem' }}>
                        <FormGroup label="Briefing / descrição" htmlFor="ed-desc">
                            <Textarea id="ed-desc" rows={2} value={val('descricao')} onChange={e => setFormData({ ...formData, descricao: e.target.value })} />
                        </FormGroup>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '0.5rem' }}>
                        <Button variant="secondary" onClick={onClose} disabled={saving}>Fechar</Button>
                        <Button variant="primary" onClick={salvarDados} isLoading={saving}>Salvar dados</Button>
                    </div>
                </section>

                {/* ── Pontos gerados ── */}
                <section className="re-section">
                    <div className="re-section-head">
                        <span className="h">Pontos gerados</span>
                        <span className="badge">{pontos.length}</span>
                    </div>
                    {loadingSub ? (
                        <div className="loading-state" style={{ padding: '1rem' }}>Carregando…</div>
                    ) : (
                        <>
                            {pontos.length === 0 && <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Nenhum ponto vinculado a este registro.</div>}
                            {pontos.map(p => (
                                <PontoRow key={p.id} ponto={p} pessoas={pessoas} onChanged={recarregarVinculos} onError={setError} />
                            ))}
                            <PontoAddRow pessoas={pessoas} tabela={tabela} referenciaId={item.id} onChanged={recarregarVinculos} onError={setError} />
                        </>
                    )}
                </section>

                {/* ── Comissões geradas ── */}
                <section className="re-section">
                    <div className="re-section-head">
                        <span className="h">Comissões geradas</span>
                        <span className="badge">{comissoes.length}</span>
                    </div>
                    {loadingSub ? (
                        <div className="loading-state" style={{ padding: '1rem' }}>Carregando…</div>
                    ) : (
                        <>
                            {comissoes.length === 0 && <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Nenhuma comissão gerada por este evento.</div>}
                            {comissoes.map(c => (
                                <ComissaoRow key={c.id} comissao={c} pessoas={pessoas} onChanged={recarregarVinculos} onError={setError} />
                            ))}
                            <ComissaoAddRow pessoas={pessoas} telefone={item.telefone} dataBase={eventoData} tabela={tabela} referenciaId={item.id} onChanged={recarregarVinculos} onError={setError} />
                        </>
                    )}
                </section>
            </div>
        </ModalOverlay>
    );
};

// ── Página ────────────────────────────────────────────────────────────────────

function normalizarEventos(agendamentos, vendas, coletados, leadsDesk, ghlInfo = {}) {
    const A = agendamentos.map(r => ({
        tipo: 'agendamento', tabela: 'ghl_reunioes', id: r.id, raw: r,
        lead: r.nome || '—', telefone: r.telefone || '', data: r.data_agendamento || '',
        responsavel: r.agendador || '—', detalhe: r.fonte || '—',
    }));
    const V = vendas.map(r => ({
        tipo: 'venda', tabela: 'ghl_vendas', id: r.id, raw: r,
        lead: r.nome || '—', telefone: r.telefone || '', data: r.data_venda || '',
        responsavel: r.vendedor || '—',
        detalhe: `${r.produto || '—'}${r.valor_total ? ` · ${formatCurrency(r.valor_total)}` : ''}`,
    }));
    const C = coletados.map(r => {
        const ghl = r.telefone_canonico ? ghlInfo[r.telefone_canonico] : null;
        const nome = r.lead_nome || ghl?.nome || null;
        const contatoId = r.ghl_contact_id || ghl?.id || null;
        return {
            tipo: 'coletado', tabela: 'ss_contatos_coletados', id: r.id,
            raw: { ...r, ghl_contact_id: contatoId },
            lead: nome || (r.telefone_canonico ? formatPhone(r.telefone_canonico) : '—'),
            telefone: r.telefone_canonico || '', data: r.data || '',
            responsavel: r.social_seller || '—',
            detalhe: [r.fonte, r.sdr_destino && `SDR: ${r.sdr_destino}`].filter(Boolean).join(' · ') || '—',
        };
    });
    const L = (leadsDesk || []).map(r => ({
        tipo: 'lead_criado', tabela: 'bd_leads_desk', id: r.id, raw: r,
        lead: r.nome || (r.telefone ? formatPhone(r.telefone) : '—'),
        telefone: r.telefone || '', data: r.data || '',
        responsavel: r.registrado_por || '—', detalhe: r.fonte || '—',
    }));
    return [...A, ...V, ...C, ...L].sort((a, b) => (b.data || '').localeCompare(a.data || ''));
}

function VinculosSecoes({ tabela, referenciaId, telefone, dataBase, pessoas, onError }) {
    const [pontos, setPontos] = useState([]);
    const [comissoes, setComissoes] = useState([]);
    const [loading, setLoading] = useState(false);
    const recarregar = useCallback(async () => {
        if (!referenciaId) return;
        try {
            const [pts, coms] = await Promise.all([
                getPontosVinculados({ tabela, referencia_id: referenciaId }),
                getComissoesVinculadas({ telefone, data: dataBase, tabela, referencia_id: referenciaId }),
            ]);
            setPontos(pts); setComissoes(coms);
        } catch (e) { onError?.(e?.message || 'Erro ao carregar pontos/comissões.'); }
    }, [tabela, referenciaId, telefone, dataBase, onError]);
    // Mesmo padrão das demais buscas da tela: abre o loading antes de recarregar pontos e
    // comissões. `recarregar` já é memoizado, então isto roda quando as chaves mudam.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { setLoading(true); recarregar().finally(() => setLoading(false)); }, [recarregar]);
    return (
        <>
            <section className="re-section">
                <div className="re-section-head"><span className="h">Pontos gerados</span><span className="badge">{pontos.length}</span></div>
                {loading ? <div className="loading-state" style={{ padding: '1rem' }}>Carregando…</div> : (<>
                    {pontos.length === 0 && <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Nenhum ponto vinculado a este registro.</div>}
                    {pontos.map(p => <PontoRow key={p.id} ponto={p} pessoas={pessoas} onChanged={recarregar} onError={onError} />)}
                    <PontoAddRow pessoas={pessoas} tabela={tabela} referenciaId={referenciaId} onChanged={recarregar} onError={onError} />
                </>)}
            </section>
            <section className="re-section">
                <div className="re-section-head"><span className="h">Comissões geradas</span><span className="badge">{comissoes.length}</span></div>
                {loading ? <div className="loading-state" style={{ padding: '1rem' }}>Carregando…</div> : (<>
                    {comissoes.length === 0 && <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>Nenhuma comissão gerada por este evento.</div>}
                    {comissoes.map(c => <ComissaoRow key={c.id} comissao={c} pessoas={pessoas} onChanged={recarregar} onError={onError} />)}
                    <ComissaoAddRow pessoas={pessoas} telefone={telefone} dataBase={dataBase} tabela={tabela} referenciaId={referenciaId} onChanged={recarregar} onError={onError} />
                </>)}
            </section>
        </>
    );
}

function ColetadoModal({ isOpen, onClose, item, pessoas, onSave }) {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    useEffect(() => {
        setForm(item ? {
            data: (item.data || '').slice(0, 10), social_seller: item.social_seller || '',
            telefone_canonico: item.telefone_canonico || '', fonte: item.fonte || '',
        } : null);
        setErr('');
    }, [item]);
    if (!isOpen || !form) return null;
    const save = async () => {
        setBusy(true); setErr('');
        try {
            const p = pessoas.find(x => x.nome === form.social_seller);
            await updateContatoColetado(item.id, { ...form, social_seller_id: p?.id ?? item.social_seller_id ?? null });
            await onSave(); onClose();
        } catch (e) { setErr(e?.message || 'Erro ao salvar.'); } finally { setBusy(false); }
    };
    return (
        <ModalOverlay isOpen={isOpen} onClose={onClose} size="wide">
            <ModalHead kicker="Número coletado" title="Editar número coletado" onClose={onClose} />
            <div style={{ padding: '1.25rem 1.5rem' }}>
                {err && <div className="alert alert-error">{err}</div>}
                <div className="grid-2">
                    <FormGroup label="Data" htmlFor="c-data">
                        <Input type="date" id="c-data" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} />
                    </FormGroup>
                    <FormGroup label="Social Seller" htmlFor="c-ss">
                        <Select id="c-ss" value={form.social_seller} onChange={e => setForm({ ...form, social_seller: e.target.value })}>
                            <option value="">—</option>
                            {pessoas.map(p => <option key={p.nome} value={p.nome}>{p.nome}</option>)}
                        </Select>
                    </FormGroup>
                    <FormGroup label="Telefone" htmlFor="c-tel">
                        <Input id="c-tel" value={form.telefone_canonico} onChange={e => setForm({ ...form, telefone_canonico: e.target.value })} />
                    </FormGroup>
                    <FormGroup label="Fonte" htmlFor="c-fonte">
                        <Input id="c-fonte" value={form.fonte} onChange={e => setForm({ ...form, fonte: e.target.value })} />
                    </FormGroup>
                </div>
                <div className="form-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button className="pill pill-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button className="pill pill-primary" onClick={save} disabled={busy}>{busy ? 'Salvando…' : 'Salvar'}</button>
                </div>
                <VinculosSecoes tabela="ss_contatos_coletados" referenciaId={item.id} telefone={item.telefone_canonico} dataBase={item.data} pessoas={pessoas} onError={setErr} />
            </div>
        </ModalOverlay>
    );
}

function LeadDeskModal({ isOpen, onClose, item, pessoas, onSave }) {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    useEffect(() => {
        setForm(item ? {
            data: (item.data || '').slice(0, 10), nome: item.nome || '',
            telefone: item.telefone || '', email: item.email || '',
            fonte: item.fonte || '', registrado_por: item.registrado_por || '',
        } : null);
        setErr('');
    }, [item]);
    if (!isOpen || !form) return null;
    const save = async () => {
        setBusy(true); setErr('');
        try { await updateLeadDesk(item.id, form); await onSave(); onClose(); }
        catch (e) { setErr(e?.message || 'Erro ao salvar.'); } finally { setBusy(false); }
    };
    return (
        <ModalOverlay isOpen={isOpen} onClose={onClose} size="narrow">
            <ModalHead kicker="Lead criado" title="Editar lead criado" onClose={onClose} />
            <div style={{ padding: '1.25rem 1.5rem' }}>
                {err && <div className="alert alert-error">{err}</div>}
                <div className="grid-2">
                    <FormGroup label="Data" htmlFor="l-data">
                        <Input type="date" id="l-data" value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} />
                    </FormGroup>
                    <FormGroup label="Nome" htmlFor="l-nome">
                        <Input id="l-nome" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
                    </FormGroup>
                    <FormGroup label="Telefone" htmlFor="l-tel">
                        <Input id="l-tel" value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })} />
                    </FormGroup>
                    <FormGroup label="E-mail" htmlFor="l-email">
                        <Input id="l-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                    </FormGroup>
                    <FormGroup label="Fonte" htmlFor="l-fonte">
                        <Input id="l-fonte" value={form.fonte} onChange={e => setForm({ ...form, fonte: e.target.value })} />
                    </FormGroup>
                    <FormGroup label="Registrado por" htmlFor="l-reg">
                        <Select id="l-reg" value={form.registrado_por} onChange={e => setForm({ ...form, registrado_por: e.target.value })}>
                            <option value="">—</option>
                            {pessoas.map(p => <option key={p.nome} value={p.nome}>{p.nome}</option>)}
                        </Select>
                    </FormGroup>
                </div>
                <div className="form-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button className="pill pill-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button className="pill pill-primary" onClick={save} disabled={busy}>{busy ? 'Salvando…' : 'Salvar'}</button>
                </div>
            </div>
        </ModalOverlay>
    );
}

export default function RegistroEventos() {
    const today = hojeSP();

    const [tab, setTab] = useState(TAB_EVENTOS);
    const [pessoas, setPessoas] = useState([]);
    const [agendamentos, setAgendamentos] = useState([]);
    const [vendas, setVendas] = useState([]);
    const [coletados, setColetados] = useState([]);
    const [leadsDesk, setLeadsDesk] = useState([]);
    const [diarios, setDiarios] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [filtroPessoa, setFiltroPessoa] = useState('');
    const [dataInicio, setDataInicio] = useState(subtractDays(today, 29));
    const [dataFim, setDataFim] = useState(today);
    const [filtroTipoEvento, setFiltroTipoEvento] = useState('');
    const [busca, setBusca] = useState('');

    const [editingItem, setEditingItem] = useState(null);
    const [editingTabela, setEditingTabela] = useState('');
    const [editingColetado, setEditingColetado] = useState(null);
    const [editingLeadDesk, setEditingLeadDesk] = useState(null);
    const [jornadaLead, setJornadaLead] = useState(null);
    const [jornadaTelefone, setJornadaTelefone] = useState('');
    const [ghlInfo, setGhlInfo] = useState({}); // telefone -> { nome, id } resolvido do GHL em background
    const ghlCacheRef = useRef(null);

    useEffect(() => { getAgendadores().then(setPessoas).catch(() => {}); }, []);

    const fetchData = useCallback(async () => {
        if (!dataInicio || !dataFim || dataInicio > dataFim) return;
        setIsLoading(true);
        setErrorMessage('');
        try {
            const [ag, vd, di, co, ld] = await Promise.all([
                getHistoricoAgendamentos(dataInicio, dataFim, filtroPessoa, '', ''),
                getHistoricoVendas(dataInicio, dataFim, filtroPessoa),
                getRegistroEnviosDiarios(dataInicio, dataFim),
                getContatosColetados(dataInicio, dataFim),
                getLeadsDesk(dataInicio, dataFim),
            ]);
            setAgendamentos(ag);
            setVendas(vd);
            setDiarios(di);
            setColetados(co);
            setLeadsDesk(ld);
        } catch (err) {
            setErrorMessage(err?.message || 'Erro ao carregar histórico.');
        } finally {
            setIsLoading(false);
        }
    }, [dataInicio, dataFim, filtroPessoa]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Resolve nome + card do contato no GHL para coletados sem nome local — em BACKGROUND,
    // fora do carregamento inicial (a busca no GHL leva ~30s e travava a aba inteira).
    // Cache de sessão evita re-buscar (guarda inclusive o "miss" = { nome:null, id:null }).
    useEffect(() => {
        const alvos = [...new Set(coletados.filter(c => !c.lead_nome && c.telefone_canonico).map(c => c.telefone_canonico))];
        if (!alvos.length) return;
        if (!ghlCacheRef.current) {
            try { ghlCacheRef.current = JSON.parse(sessionStorage.getItem('ghlContatoCache') || '{}'); }
            catch { ghlCacheRef.current = {}; }
        }
        const cache = ghlCacheRef.current;
        const doCache = {};
        const faltantes = [];
        alvos.forEach(t => { if (t in cache) doCache[t] = cache[t]; else faltantes.push(t); });
        if (Object.keys(doCache).length) setGhlInfo(prev => ({ ...prev, ...doCache }));
        if (!faltantes.length) return;
        let cancelado = false;
        getGhlContatoInfo(faltantes).then(info => {
            if (cancelado) return;
            const novos = {};
            faltantes.forEach(t => { novos[t] = info[t] || { nome: null, id: null }; });
            ghlCacheRef.current = { ...cache, ...novos };
            try { sessionStorage.setItem('ghlContatoCache', JSON.stringify(ghlCacheRef.current)); } catch { /* quota */ }
            setGhlInfo(prev => ({ ...prev, ...novos }));
        });
        return () => { cancelado = true; };
    }, [coletados]);

    const agendamentosFiltrados = agendamentos.filter(r => matchBusca(r, busca));
    const vendasFiltradas = vendas.filter(r => matchBusca(r, busca));

    const eventos = normalizarEventos(agendamentosFiltrados, vendasFiltradas, coletados, leadsDesk, ghlInfo);
    const eventosFiltrados = eventos.filter(e => {
        if (filtroTipoEvento && e.tipo !== filtroTipoEvento) return false;
        if (e.tipo === 'coletado' || e.tipo === 'lead_criado') {
            if (filtroPessoa && e.responsavel !== filtroPessoa) return false;
            if (busca && !(`${e.lead} ${e.telefone}`).toLowerCase().includes(busca.toLowerCase())) return false;
        }
        return true;
    });

    const faturamento = vendasFiltradas.reduce((s, r) => s + Number(r.valor_total || 0), 0);

    const handleDeleteClick = async (item, tabela) => {
        const rotulo = tabela === 'ghl_vendas' ? 'esta venda' : 'este agendamento';
        if (!confirm(`Apagar ${rotulo} de ${item.nome || 'lead'}? A linha e o evento de origem serão removidos (não volta no reprocessamento).`)) return;
        setIsLoading(true);
        setErrorMessage('');
        try {
            await deleteRegistro({ tabela, id: item.id, evento_origem_id: item.evento_origem_id || null, ghl_opportunity_id: item.ghl_opportunity_id || null });
            await fetchData();
        } catch (err) {
            setErrorMessage(err?.message || 'Erro ao apagar registro.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = (item, tabela) => {
        if (tabela === 'ss_contatos_coletados') { setEditingColetado(item); return; }
        if (tabela === 'bd_leads_desk') { setEditingLeadDesk(item); return; }
        setEditingItem(item); setEditingTabela(tabela);
    };
    const handleDeleteLeadDesk = async (item) => {
        if (!confirm(`Apagar o lead criado "${item.nome || item.telefone || 'lead'}"?`)) return;
        setIsLoading(true); setErrorMessage('');
        try {
            await deleteLeadDesk(item.id);
            await fetchData();
        } catch (err) {
            setErrorMessage(err?.message || 'Erro ao apagar lead.');
        } finally { setIsLoading(false); }
    };
    const handleDeleteColetado = async (item) => {
        if (!confirm(`Apagar o número coletado de ${formatPhone(item.telefone_canonico) || 'lead'}?`)) return;
        setIsLoading(true); setErrorMessage('');
        try {
            await deleteContatoColetado(item.id);
            await fetchData();
        } catch (err) {
            setErrorMessage(err?.message || 'Erro ao apagar número coletado.');
        } finally { setIsLoading(false); }
    };
    const handleJornadaClick = (lead, telefone) => { setJornadaLead(lead); setJornadaTelefone(telefone); };

    const contagem = tab === TAB_EVENTOS ? eventosFiltrados.length : null;

    return (
        <div className="re-central">
            <style>{scopedCSS}</style>

            <div className="page-header">
                <div className="page-header-left">
                    <div>
                        <div className="page-kicker">Central de Controle</div>
                        <h1 className="page-title">Registro de Eventos</h1>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="re-command">
                    <div className="re-lead">
                        <span className="re-sub">
                            Período <strong style={{ color: 'var(--ink)' }}>{formatDate(dataInicio)}</strong> — <strong style={{ color: 'var(--ink)' }}>{formatDate(dataFim)}</strong>
                            {contagem != null && <> · <strong style={{ color: 'var(--emerald)' }}>{contagem}</strong> registro(s)</>}
                        </span>
                    </div>
                </div>

                {errorMessage && <div className="alert alert-error">{errorMessage}</div>}

                <div className="filter-bar" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                    <FormGroup label="Buscar lead" htmlFor="busca">
                        <Input id="busca" type="search" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Nome, telefone ou e-mail" />
                    </FormGroup>
                    <FormGroup label="Pessoa" htmlFor="filtroPessoa">
                        <Select id="filtroPessoa" value={filtroPessoa} onChange={e => setFiltroPessoa(e.target.value)}>
                            <option value="">Todas</option>
                            {pessoas.map(p => <option key={p.nome} value={p.nome}>{p.nome}</option>)}
                        </Select>
                    </FormGroup>
                    <FormGroup label="Data início" htmlFor="dataInicio">
                        <Input type="date" id="dataInicio" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
                    </FormGroup>
                    <FormGroup label="Data fim" htmlFor="dataFim">
                        <Input type="date" id="dataFim" value={dataFim} onChange={e => setDataFim(e.target.value)} />
                    </FormGroup>
                    {tab === TAB_EVENTOS && (
                        <FormGroup label="Tipo de evento" htmlFor="filtroTipoEvento">
                            <Select id="filtroTipoEvento" value={filtroTipoEvento} onChange={e => setFiltroTipoEvento(e.target.value)}>
                                <option value="">Todos</option>
                                <option value="agendamento">Agendamentos</option>
                                <option value="venda">Vendas</option>
                                <option value="coletado">Números coletados</option>
                                <option value="lead_criado">Leads criados</option>
                            </Select>
                        </FormGroup>
                    )}
                </div>

                {!isLoading && tab === TAB_EVENTOS && (
                    <div className="re-kpis">
                        <div className="re-kpi">
                            <div className="k-label">Agendamentos</div>
                            <div className="k-value" style={{ fontSize: '1.5rem', color: 'var(--emerald)' }}>{agendamentosFiltrados.length}</div>
                        </div>
                        <div className="re-kpi">
                            <div className="k-label">Vendas</div>
                            <div className="k-value" style={{ fontSize: '1.5rem', color: 'var(--emerald)' }}>{vendasFiltradas.length}</div>
                        </div>
                        <div className="re-kpi">
                            <div className="k-label">Números coletados</div>
                            <div className="k-value" style={{ fontSize: '1.5rem' }}>{coletados.length}</div>
                        </div>
                        <div className="re-kpi">
                            <div className="k-label">Leads criados</div>
                            <div className="k-value" style={{ fontSize: '1.5rem' }}>{leadsDesk.length}</div>
                        </div>
                        <div className="re-kpi">
                            <div className="k-label">Faturamento</div>
                            <div className="k-value" style={{ fontSize: '1.25rem', color: 'var(--emerald)' }}>{formatCurrency(faturamento)}</div>
                        </div>
                    </div>
                )}

                <div className="card" style={{ padding: '1.25rem' }}>
                    <div className="tab-bar">
                        <button className={`tab-btn${tab === TAB_EVENTOS ? ' active' : ''}`} onClick={() => setTab(TAB_EVENTOS)}>
                            Eventos ({eventosFiltrados.length})
                        </button>
                        <button className={`tab-btn${tab === TAB_DIARIOS ? ' active' : ''}`} onClick={() => setTab(TAB_DIARIOS)}>
                            Diários
                        </button>
                    </div>

                    {isLoading ? (
                        <div className="loading-state">Carregando registros…</div>
                    ) : tab === TAB_DIARIOS ? (
                        <DiarioTab diarios={diarios} dataInicio={dataInicio} dataFim={dataFim} />
                    ) : eventosFiltrados.length === 0 ? (
                        <div className="empty-state"><div className="empty-state-icon">📭</div><div className="empty-state-title">Nenhum evento no período.</div></div>
                    ) : (
                        <div className="re-tablewrap">
                            <table className="re-table">
                                <thead>
                                    <tr>
                                        <th>Tipo</th>
                                        <th>Lead</th>
                                        <th>Telefone</th>
                                        <th>Data</th>
                                        <th>Responsável</th>
                                        <th>Detalhe</th>
                                        <th style={{ textAlign: 'right' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {eventosFiltrados.map(e => (
                                        <tr key={`${e.tabela}-${e.id}`}>
                                            <td><span className={TIPO_CHIP[e.tipo]}>{TIPO_LABEL[e.tipo]}</span></td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    <span className="re-avatar">{initial(e.lead)}</span>
                                                    <span className="re-leadname" onClick={() => handleJornadaClick(e.raw, e.telefone)}>{e.lead}</span>
                                                </div>
                                            </td>
                                            <td className="re-num" style={{ color: 'var(--muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatPhone(e.telefone)}</td>
                                            <td className="re-num" style={{ whiteSpace: 'nowrap' }}>{formatDate(e.data)}</td>
                                            <td>{e.responsavel}</td>
                                            <td style={{ fontSize: '0.85rem' }}>{e.detalhe}</td>
                                            <td>
                                                <div className="re-actions">
                                                    {e.raw.ghl_opportunity_id
                                                        ? <GhlLink oppId={e.raw.ghl_opportunity_id} label="GHL" className="re-iconbtn" />
                                                        : e.raw.ghl_contact_id
                                                            ? <GhlLink contactId={e.raw.ghl_contact_id} label="GHL" className="re-iconbtn" />
                                                            : null}
                                                    <button className="re-iconbtn" onClick={() => handleJornadaClick(e.raw, e.telefone)}>Jornada</button>
                                                    <button className="re-iconbtn" onClick={() => handleEditClick(e.raw, e.tabela)}>Editar</button>
                                                    {e.tipo === 'coletado'
                                                        ? <button className="re-iconbtn danger" onClick={() => handleDeleteColetado(e.raw)}>Apagar</button>
                                                        : e.tipo === 'lead_criado'
                                                        ? <button className="re-iconbtn danger" onClick={() => handleDeleteLeadDesk(e.raw)}>Apagar</button>
                                                        : <button className="re-iconbtn danger" onClick={() => handleDeleteClick(e.raw, e.tabela)}>Apagar</button>}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <JornadaPopup isOpen={!!jornadaLead} onClose={() => setJornadaLead(null)} lead={jornadaLead} telefone={jornadaTelefone} />
            <EditModal isOpen={!!editingItem} onClose={() => setEditingItem(null)} item={editingItem} tabela={editingTabela} onSave={fetchData} />
            <ColetadoModal isOpen={!!editingColetado} onClose={() => setEditingColetado(null)} item={editingColetado} pessoas={pessoas} onSave={fetchData} />
            <LeadDeskModal isOpen={!!editingLeadDesk} onClose={() => setEditingLeadDesk(null)} item={editingLeadDesk} pessoas={pessoas} onSave={fetchData} />
        </div>
    );
}

// ── Aba Diários (conteúdo do antigo Registro de Envios) ─────────────────────────

function DiarioTab({ diarios, dataInicio, dataFim }) {
    const [pessoas, setPessoas] = useState([]);
    const [filtroNome, setFiltroNome] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('');

    useEffect(() => {
        getAgendadores()
            .then(data => setPessoas(data.filter(p => {
                const c = (p.cargo || '').trim().toLowerCase();
                return c === 'sdr' || c === 'social seller' || c === 'closer';
            })))
            .catch(() => {});
    }, []);

    const submittedSet = useMemo(() => {
        const s = new Set();
        diarios.forEach(e => s.add(`${e.data}|${e.nome}|${e.tipo}`));
        return s;
    }, [diarios]);

    const datesInRange = useMemo(() => {
        if (!dataInicio || !dataFim) return [];
        const dates = [];
        let cur = new Date(dataInicio + 'T12:00:00');
        const end = new Date(dataFim + 'T12:00:00');
        while (cur <= end) { dates.push(cur.toISOString().split('T')[0]); cur.setDate(cur.getDate() + 1); }
        return dates.reverse();
    }, [dataInicio, dataFim]);

    const pessoasFiltradas = useMemo(() => pessoas.filter(p => {
        const tipo = CARGO_TO_TIPO[(p.cargo || '').trim().toLowerCase()] || '';
        if (filtroTipo && tipo !== filtroTipo) return false;
        if (filtroNome && p.nome !== filtroNome) return false;
        return true;
    }), [pessoas, filtroNome, filtroTipo]);

    const entryMap = useMemo(() => {
        const m = {};
        diarios.forEach(e => { m[`${e.data}|${e.nome}|${e.tipo}`] = e.created_at; });
        return m;
    }, [diarios]);

    const rows = useMemo(() => {
        const result = [];
        for (const date of datesInRange) {
            for (const p of pessoasFiltradas) {
                const tipo = CARGO_TO_TIPO[(p.cargo || '').trim().toLowerCase()] || p.cargo;
                const key = `${date}|${p.nome}|${tipo}`;
                result.push({ date, nome: p.nome, tipo, enviado: submittedSet.has(key), created_at: entryMap[key] || null });
            }
        }
        return result;
    }, [datesInRange, pessoasFiltradas, submittedSet, entryMap]);

    const totalEnviados = rows.filter(r => r.enviado).length;
    const totalEsperados = rows.length;
    const taxaEnvio = totalEsperados > 0 ? Math.round((totalEnviados / totalEsperados) * 100) : 0;

    return (
        <>
            <div style={{ marginBottom: '1.25rem' }}>
                <div className="filter-bar" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                    <FormGroup label="Tipo de diário" htmlFor="filtroTipoDiario">
                        <Select id="filtroTipoDiario" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                            <option value="">Todos</option>
                            <option value="SDR">SDR</option>
                            <option value="Social Seller">Social Seller</option>
                            <option value="Closer">Closer</option>
                        </Select>
                    </FormGroup>
                    <FormGroup label="Pessoa" htmlFor="filtroNomeDiario">
                        <Select id="filtroNomeDiario" value={filtroNome} onChange={e => setFiltroNome(e.target.value)}>
                            <option value="">Todas</option>
                            {pessoasFiltradas.map(p => <option key={p.nome} value={p.nome}>{p.nome}</option>)}
                        </Select>
                    </FormGroup>
                </div>

                {totalEsperados > 0 && (
                    <div className="re-kpis" style={{ marginTop: '1.25rem' }}>
                        <div className="re-kpi">
                            <div className="k-label">Enviados</div>
                            <div className="k-value" style={{ fontSize: '1.5rem', color: 'var(--emerald)' }}>{totalEnviados}</div>
                        </div>
                        <div className="re-kpi">
                            <div className="k-label">Não enviados</div>
                            <div className="k-value" style={{ fontSize: '1.5rem', color: 'var(--danger-soft)' }}>{totalEsperados - totalEnviados}</div>
                        </div>
                        <div className="re-kpi">
                            <div className="k-label">Taxa de envio</div>
                            <div className="k-value" style={{ fontSize: '1.5rem' }}>{taxaEnvio}%</div>
                        </div>
                    </div>
                )}
            </div>

            {rows.length === 0 ? (
                <div className="empty-state"><div className="empty-state-title">Nenhum registro encontrado para o período.</div></div>
            ) : (
                <div className="re-tablewrap">
                    <table className="re-table">
                        <thead>
                            <tr>
                                <th>Data</th>
                                <th>Nome</th>
                                <th>Tipo</th>
                                <th style={{ textAlign: 'center' }}>Status</th>
                                <th>Horário</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => (
                                <tr key={i}>
                                    <td className="re-num">{formatDate(row.date)}</td>
                                    <td>{row.nome}</td>
                                    <td><span className={CHIP_CLASSES[row.tipo] || 'chip chip-muted'}>{row.tipo}</span></td>
                                    <td style={{ textAlign: 'center' }}>
                                        {row.enviado
                                            ? <span style={{ color: 'var(--emerald)', fontWeight: 600, fontSize: '0.8rem' }}>✓ Enviado</span>
                                            : <span style={{ color: 'var(--danger-soft)', fontWeight: 600, fontSize: '0.8rem' }}>✗ Não enviado</span>}
                                    </td>
                                    <td className="re-num" style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>{row.created_at ? formatTime(row.created_at) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
